import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import UploadIcon from "@mui/icons-material/Upload";
import ImageIcon from "@mui/icons-material/Image";

import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import ENV from "../../../config/env";
import {
  listProyectos,
  createProyecto,
  updateProyecto,
  deleteProyecto,
} from "../services/telusgyProyectosService";

const BRANCHES = ["La Paz", "Santa Cruz", "Cochabamba"];

const EMPTY_FORM = {
  title: "",
  city: "",
  branch: BRANCHES[0],
  kwpInstalled: "",
  problem: "",
  solution: "",
  result: "",
  tags: "",
};

const getError = (error) =>
  error?.response?.data?.error || error?.message || "No se pudo completar la operacion.";

const resolveImage = (imageUrl) => {
  if (!imageUrl) return null;
  return imageUrl.startsWith("http") ? imageUrl : `${ENV.API_BASE_URL}${imageUrl}`;
};

export default function TelusgyProyectosPage() {
  const [proyectos, setProyectos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listProyectos();
      setProyectos(Array.isArray(data) ? data : []);
    } catch (error) {
      setToast({ open: true, severity: "error", message: getError(error) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setImageFile(null);
    setImagePreview(null);
    setOpen(true);
  };

  const openEdit = (proyecto) => {
    setEditingId(proyecto.id);
    setForm({
      title: proyecto.title,
      city: proyecto.city,
      branch: BRANCHES.includes(proyecto.branch) ? proyecto.branch : BRANCHES[0],
      kwpInstalled: String(proyecto.kwpInstalled ?? ""),
      problem: proyecto.problem || "",
      solution: proyecto.solution || "",
      result: proyecto.result || "",
      tags: Array.isArray(proyecto.tags) ? proyecto.tags.join(", ") : "",
    });
    setImageFile(null);
    setImagePreview(resolveImage(proyecto.imageUrl));
    setOpen(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.city.trim()) {
      setToast({ open: true, severity: "warning", message: "Título y ciudad son obligatorios." });
      return;
    }

    const fd = new FormData();
    fd.append("title", form.title.trim());
    fd.append("city", form.city.trim());
    fd.append("branch", form.branch);
    fd.append("kwpInstalled", form.kwpInstalled || "0");
    fd.append("problem", form.problem.trim());
    fd.append("solution", form.solution.trim());
    fd.append("result", form.result.trim());
    fd.append(
      "tags",
      form.tags.split(",").map((t) => t.trim()).filter(Boolean).join(",")
    );
    if (imageFile) fd.append("imagen", imageFile);

    setSaving(true);
    try {
      if (editingId) {
        await updateProyecto(editingId, fd);
        setToast({ open: true, severity: "success", message: "Proyecto actualizado." });
      } else {
        await createProyecto(fd);
        setToast({ open: true, severity: "success", message: "Proyecto creado." });
      }
      setOpen(false);
      load();
    } catch (error) {
      setToast({ open: true, severity: "error", message: getError(error) });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este proyecto?")) return;
    try {
      await deleteProyecto(id);
      setToast({ open: true, severity: "success", message: "Proyecto eliminado." });
      load();
    } catch (error) {
      setToast({ open: true, severity: "error", message: getError(error) });
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Proyectos Telusgy"
        subtitle="Casos reales mostrados en telusgy.com.bo"
        backTo="/dashboard"
        actions={(
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton onClick={load} disabled={loading}>
              <RefreshIcon />
            </IconButton>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ textTransform: "none" }}>
              Nuevo proyecto
            </Button>
          </Box>
        )}
      />

      <Box sx={{ p: { xs: 1, md: 1.5 } }}>
        <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: "hidden" }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Imagen</TableCell>
                  <TableCell>Título</TableCell>
                  <TableCell>Ciudad</TableCell>
                  <TableCell>Sucursal</TableCell>
                  <TableCell>kWp instalado</TableCell>
                  <TableCell>Resultado</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && !proyectos.length && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      No hay proyectos registrados todavía.
                    </TableCell>
                  </TableRow>
                )}
                {proyectos.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell>
                      <Avatar variant="rounded" src={resolveImage(p.imageUrl)}>
                        <ImageIcon fontSize="small" />
                      </Avatar>
                    </TableCell>
                    <TableCell>{p.title}</TableCell>
                    <TableCell>{p.city}</TableCell>
                    <TableCell>{p.branch}</TableCell>
                    <TableCell>{p.kwpInstalled}</TableCell>
                    <TableCell>{p.result || "-"}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit(p)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(p.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        {!loading && !!proyectos.length && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            {proyectos.length} proyecto(s) en total.
          </Typography>
        )}
      </Box>

      <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? "Editar proyecto" : "Nuevo proyecto"}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 1.25, pt: 0.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar variant="rounded" src={imagePreview} sx={{ width: 64, height: 64 }}>
                <ImageIcon />
              </Avatar>
              <Button
                variant="outlined"
                size="small"
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ textTransform: "none" }}
              >
                Subir imagen
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                hidden
                onChange={handleFileChange}
              />
            </Box>
            <TextField
              size="small"
              label="Título *"
              value={form.title}
              onChange={(e) => setForm((c) => ({ ...c, title: e.target.value }))}
            />
            <TextField
              size="small"
              label="Ciudad *"
              value={form.city}
              onChange={(e) => setForm((c) => ({ ...c, city: e.target.value }))}
            />
            <TextField
              select
              size="small"
              label="Sucursal *"
              value={form.branch}
              onChange={(e) => setForm((c) => ({ ...c, branch: e.target.value }))}
            >
              {BRANCHES.map((b) => (
                <MenuItem key={b} value={b}>{b}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              type="number"
              label="kWp instalado"
              value={form.kwpInstalled}
              onChange={(e) => setForm((c) => ({ ...c, kwpInstalled: e.target.value }))}
            />
            <TextField
              size="small"
              multiline
              minRows={2}
              label="El Desafío"
              value={form.problem}
              onChange={(e) => setForm((c) => ({ ...c, problem: e.target.value }))}
            />
            <TextField
              size="small"
              multiline
              minRows={2}
              label="Nuestra Solución"
              value={form.solution}
              onChange={(e) => setForm((c) => ({ ...c, solution: e.target.value }))}
            />
            <TextField
              size="small"
              label="Resultado (badge)"
              placeholder="Ej: Ahorro del 85% mensual"
              value={form.result}
              onChange={(e) => setForm((c) => ({ ...c, result: e.target.value }))}
            />
            <TextField
              size="small"
              label="Etiquetas (separadas por coma)"
              placeholder="Residencial, Ahorro"
              value={form.tags}
              onChange={(e) => setForm((c) => ({ ...c, tags: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button>
          <LoadingButton variant="contained" loading={saving} onClick={handleSave}>
            Guardar
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4500}
        onClose={() => setToast((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast((current) => ({ ...current, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
